import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { extend } from 'lodash';
import { forkJoin, Observable, of } from 'rxjs';
import { AUTH_KEYS } from '../auth/auth-keys.constant';
import { BrowserManagerDataService } from '../browser-manager-data/browser-manager-data.service';
import { CONTENT_TYPE_HEADER, IResponseInfo } from '../http/http.data';
import { HttpService } from '../http/http.service';
import { NavigationService } from '../navigation/navigation.service';
import { LoggerService } from '../shared/services/logger/logger.service';
import { IAuth, IRefreshAuth } from './auth-data.data';
import { AuthDataDispatcher } from './auth-data.dispatcher';
import { AuthDataStore } from './auth-data.store';
import {
  catchError,
  filter,
  finalize,
  flatMap,
  map,
  share,
  take,
} from 'rxjs/operators';
import { EnvironmentsEnum } from '../../../environments/environments.enum';
import { environment } from '../../../environments/environment';

@Injectable()
export class AuthDataService {
  private _loggingOut = false;
  private _authUrl = '/auth/identity/connect/token';
  private _authHeaders$: Observable<any> = this._browserManagerDataService
    .getBrowserInfoHeaders()
    .pipe(
      map((browserInfoHeaders: any) =>
        extend(browserInfoHeaders, {
          [CONTENT_TYPE_HEADER]: 'application/x-www-form-urlencoded',
        })
      ),
      take(1)
    );
  private _baseUrl: string = environment.gatewayUrl;
  private _refreshTokenRequest: Observable<string>;
  private _redirectPath: string;

  // TODO: encode these
  private _clientId = 'EDB34FDB-FE26-477C-BC62-30C7C42EEDF6';
  private _clientSecret = 'daniel2go';

  constructor(
    private _navigationService: NavigationService,
    private _loggerService: LoggerService,
    private _httpService: HttpService,
    private _authDataDispatcher: AuthDataDispatcher,
    private _browserManagerDataService: BrowserManagerDataService,
    private _authDataStore: AuthDataStore
  ) {}

  login(
    username: string,
    password: string,
    accountId?: string
  ): Observable<any> {
    if (environment.env === EnvironmentsEnum.localdev) {
      const url = Location.joinWithSlash(this._baseUrl, this._authUrl);
      const payload: IAuth = {
        client_id: this._clientId,
        client_secret: this._clientSecret,
        grant_type: 'password',
        scope: 'read write offline_access',
        username: username,
        password: password,
      };

      if (accountId) {
        extend(payload, { acr_values: `accountId:${accountId}` });
      }

      return this._authHeaders$.pipe(
        flatMap((authHeaders: any) =>
          this._httpService.post(url, payload, authHeaders).pipe(
            flatMap(({ body }: IResponseInfo) => {
              const accounts = body.accounts || [];

              // we need to send accountId to OAuth server so it generates a valid access_token
              if (!accountId && accounts.length > 0) {
                extend(payload, {
                  acr_values: `accountId:${accounts[0].account_id}`,
                });

                return this._httpService
                  .post(url, payload, authHeaders)
                  .pipe(map((response: IResponseInfo) => response.body));
              }

              return of(body);
            }),
            map((responseBody: any) => {
              const accounts = responseBody.accounts || [];

              if (accounts.length > 0) {
                this.updateTokens(responseBody)
                  .pipe(filter((x) => !!x))
                  .subscribe(() => {
                    const redirectPath = this._redirectPath;

                    this._loggerService.log(
                      'redirecting user to:',
                      redirectPath || '/'
                    );

                    this._authDataDispatcher.authenticated();

                    if (redirectPath) {
                      this._authDataDispatcher.setRedirectPath(null);
                    }

                    setTimeout(() =>
                      this._navigationService.goTo(redirectPath || '/')
                    );
                  });

                return accounts.slice(0, 1);
              }

              return accounts;
            })
          )
        ),
        share()
      );
    } else {
      return of([]);
    }
  }

  refreshAccessToken(): Observable<string> {
    if (!this._refreshTokenRequest) {
      this._loggerService.log('creating refresh token payload');

      this._refreshTokenRequest = forkJoin([
        this._authDataStore.getRefreshToken(),
        this._authHeaders$,
      ]).pipe(
        flatMap(([refreshToken, authHeaders]) => {
          const payload: IRefreshAuth = {
            client_id: this._clientId,
            client_secret: this._clientSecret,
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
          };

          this._loggerService.log('creating refresh token request');

          return this._httpService.post(
            Location.joinWithSlash(this._baseUrl, this._authUrl),
            payload,
            authHeaders
          );
        }),
        flatMap((res: IResponseInfo) => {
          const response = res.body;

          return this.updateTokens(response).pipe(
            map((updated: boolean) =>
              updated ? response[AUTH_KEYS.ACCESS_TOKEN] : null
            )
          );
        }),
        catchError(() => of(null)),
        finalize(() => (this._refreshTokenRequest = null)),
        share()
      );
    }

    return this._refreshTokenRequest;
  }

  updateTokens(response: any): Observable<boolean> {
    const accessToken = response[AUTH_KEYS.ACCESS_TOKEN];
    const refreshToken = response[AUTH_KEYS.REFRESH_TOKEN];

    if (!this._loggingOut && accessToken && refreshToken) {
      this._authDataDispatcher.updateTokens(accessToken, refreshToken);

      return of(true);
    }

    return of(false);
  }
}
